import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ToastProvider } from '@/context/ToastContext';
import { AppProvider } from '@/context/AppContext';
import { useApp } from '@/context/AppContext';
import Navbar from '@/components/Navbar';

// Lazy-loaded pages for code splitting
const HomePage = lazy(() => import('@/pages/Home'));
const SearchPage = lazy(() => import('@/pages/Search'));
const PropertyDetail = lazy(() => import('@/pages/PropertyDetail'));
const AuthPage = lazy(() => import('@/pages/Auth'));
const MessagesPage = lazy(() => import('@/pages/Messages'));
const SupportPage = lazy(() => import('@/pages/Support'));
const PricingPage = lazy(() => import('@/pages/Pricing'));
const AdminDashboard = lazy(() => import('@/pages/dashboards/AdminDashboard'));
const LandlordDashboard = lazy(() => import('@/pages/dashboards/LandlordDashboard'));
const TenantDashboard = lazy(() => import('@/pages/dashboards/TenantDashboard'));
const AdminLayout = lazy(() => import('@/admin/AdminLayout'));
const AdminLogin = lazy(() => import('@/admin/AdminLogin'));
const RolesPage = lazy(() => import('@/admin/pages/RolesPage'));
const AuditPage = lazy(() => import('@/admin/pages/AuditPage'));
const ApprovalsPage = lazy(() => import('@/admin/pages/ApprovalsPage'));
const AiManagementPage = lazy(() => import('@/admin/pages/AiManagementPage'));
const ReportsPage = lazy(() => import('@/admin/pages/ReportsPage'));
const SettingsPage = lazy(() => import('@/admin/pages/SettingsPage'));
const ModerationPage = lazy(() => import('@/admin/pages/ModerationPage'));

const DashboardRedirect: React.FC = () => {
  const { userProfile } = useApp();
  if (!userProfile) return <Navigate to="/login" />;
  if (userProfile.role === 'admin' || userProfile.role === 'super_admin') return <Navigate to="/admin" />;
  if (userProfile.role === 'landlord') return <LandlordDashboard />;
  return <TenantDashboard />;
};

const PublicLayout: React.FC = () => (
  <div className="min-h-screen bg-dark text-slate-200 font-sans selection:bg-primary/30">
    <Navbar />
    <div className="w-full"><Outlet /></div>
  </div>
);

const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App: React.FC = () => (
  <ToastProvider>
    <AppProvider>
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Dedicated admin panel — own layout, no public navbar */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/overview" replace />} />
              <Route path="approvals" element={<ApprovalsPage />} />
              <Route path="ai" element={<AiManagementPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="tickets" element={<SupportPage embedded />} />
              <Route path="moderation" element={<ModerationPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="roles" element={<RolesPage />} />
              <Route path="audit" element={<AuditPage />} />
              <Route path=":section" element={<AdminDashboard />} />
            </Route>

            {/* Public + tenant/landlord — with navbar */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/property/:id" element={<PropertyDetail />} />
              <Route path="/login" element={<AuthPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/dashboard" element={<DashboardRedirect />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </AppProvider>
  </ToastProvider>
);

export default App;
